import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import type { CmsRestaurantRow } from './types';

const endpoint = 'ums/v1/restaurants';
export const getCmsRestaurantsEndpointIdentifier = endpoint;

export interface CmsRestaurantsResponse {
  data: CmsRestaurantRow[];
  count: number;
  total: number;
  page: number;
  pageCount: number;
  hasNext: boolean;
  hasPrev: boolean;
}

type TData = CmsRestaurantsResponse;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { page: TParam; limit: TParam; name: TParam; accountManagerId: TParam; sort: TParam }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

// `sort` is already BaseSortButton's `field.asc|desc`, which is the UMS contract, so it is
// passed through untouched; the endpoint defaults to id.desc when it is absent.
export function useGetCmsRestaurantsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { name, accountManagerId, sort } = getPageParams();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, {
        params: { page, limit, name, accountManagerId, sort },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint, { page, limit, name, accountManagerId, sort }], queryFn, {
    enabled: isLoggedIn,
    keepPreviousData: true,
    ...options,
  });
}
